import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SocialPost } from '../api/client'
import { getSocial, socialPost, socialPublishAll, socialSave } from './api'
import { useEditorialTitle } from './titleContext'
import { etDate, etTime } from './labels'
import { pieceHref } from './links'
import { POSTING_OFF, bannerText, canPostApproved, parseSocialShow, showActions, tabLabels } from './social'
import { describeError } from '../lib/describeError'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const btn = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3 font-ui text-[13px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const btnPrimary = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-3 font-ui text-[13px] font-semibold text-ink-900 hover:opacity-90 disabled:opacity-50 ${focus}`
const chip = 'inline-flex h-6 items-center rounded-full px-2.5 font-ui text-[12px] font-semibold whitespace-nowrap'

const STATE: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-ink-600 text-paper-dim' },
  approved: { label: 'Approved', cls: 'bg-radar-green/15 text-radar-green' },
  published: { label: 'Posted', cls: 'bg-radar-cyan/15 text-radar-cyan' },
  rejected: { label: 'Dropped', cls: 'bg-ink-600 text-paper-dim' },
  failed: { label: 'Failed', cls: 'bg-radar-red/15 text-radar-red' },
}

function PostCard({ draftId, post, postingOn, onChange }: { draftId: number; post: SocialPost; postingOn: boolean; onChange: () => void }) {
  const [edit, setEdit] = useState<string | null>(null)
  const body = edit ?? post.body
  const dirty = edit !== null && edit !== post.body
  const done = () => { setEdit(null); onChange() }
  const act = useMutation({ mutationFn: (verb: 'rewrite' | 'approve' | 'reject' | 'publish') => socialPost(draftId, post.platform, verb), onSuccess: done })
  const save = useMutation({ mutationFn: () => socialSave(draftId, post.platform, body), onSuccess: done })
  const a = showActions(postingOn, post, dirty)
  const st = STATE[post.status] ?? STATE.draft
  const err = act.error ?? save.error
  const busy = act.isPending || save.isPending
  return (
    <article className="flex flex-col gap-2.5 rounded-lg border border-ink-500 bg-ink-800 p-3.5">
      <div className="flex items-center justify-between gap-2">
        <strong className="font-ui text-[14px] text-paper">{post.label}</strong>
        <span className={`${chip} ${st.cls}`}>{st.label}</span>
      </div>
      {post.ambiguous && <p className="m-0 font-ui text-[13px] text-amber-deep">This may already be live. Check the page before posting again.</p>}
      {post.last_error && !post.ambiguous && <p className="m-0 font-ui text-[13px] text-radar-red">{post.last_error}</p>}
      <label className="flex flex-col">
        <span className="sr-only">{post.label} post</span>
        <textarea rows={4} disabled={post.status === 'published'} value={body} onChange={(e) => setEdit(e.target.value)}
          className={`w-full resize-y rounded-lg border border-ink-400 bg-ink-700 p-2.5 font-ui text-[14px] text-paper ${focus}`} />
      </label>
      <span className={`font-ui text-[12px] ${post.over_by > 0 ? 'text-radar-red' : 'text-paper-dim'}`}>
        {post.length.toLocaleString('en-US')} of {post.limit.toLocaleString('en-US')}{post.over_by > 0 ? `, over by ${post.over_by}` : ''}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {a.save && <button type="button" className={btnPrimary} disabled={busy} onClick={() => save.mutate()}>Save</button>}
        {a.approve && <button type="button" className={btnPrimary} disabled={busy} onClick={() => act.mutate('approve')}>Approve</button>}
        {a.publish && <button type="button" className={btn} disabled={busy} onClick={() => act.mutate('publish')}>Publish</button>}
        {a.rewrite && <button type="button" className={btn} disabled={busy} onClick={() => act.mutate('rewrite')}>Rewrite</button>}
        {a.drop && <button type="button" className={`${btn} text-radar-red`} disabled={busy} onClick={() => act.mutate('reject')}>Drop</button>}
        {post.permalink && <a href={post.permalink} target="_blank" rel="noopener noreferrer" className={btn}>View the post</a>}
      </div>
      {!a.publish && postingOn && post.status === 'approved' && post.blocked_reason && <span className="font-ui text-[12px] text-paper-dim">{post.blocked_reason}</span>}
      {err && <p role="alert" className="m-0 font-ui text-[13px] text-radar-red">{describeError(err)}</p>}
    </article>
  )
}

export function SocialPage() {
  const { section, publicationSlug } = useEditorialTitle()
  const [params, setParams] = useSearchParams()
  const show = parseSocialShow(params.get('show'))
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['editorial', 'social', publicationSlug, show], queryFn: () => getSocial(publicationSlug, show) })
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['editorial', 'social', publicationSlug] }) }
  const all = useMutation({ mutationFn: (id: number) => socialPublishAll(id), onSuccess: refresh })
  const d = q.data
  const banner = d ? bannerText(d.posting_on) : null

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <h1 className="m-0 font-serif text-[34px] font-medium leading-tight text-paper">Posts for each piece</h1>
          <p className="m-0 font-ui text-paper-dim">Written with every piece. Approve the ones you like.</p>
        </div>
        <span className="flex flex-wrap gap-1.5">
          {d?.accounts.map((a) => {
            const name = a.platform === 'x' ? 'X' : a.platform.charAt(0).toUpperCase() + a.platform.slice(1)
            return <span key={a.platform} className={`${chip} ${a.connected ? 'bg-radar-green/15 text-radar-green' : 'bg-ink-600 text-paper-dim'}`}>{name} {a.connected ? 'connected' : 'not connected'}</span>
          })}
        </span>
      </header>
      {banner && <div role="status" className="rounded-[10px] bg-ink-600 px-4 py-3.5 font-ui text-[14px] text-paper">{banner || POSTING_OFF}</div>}
      <div role="tablist" aria-label="Show" className="flex flex-wrap gap-1 self-start rounded-[10px] bg-ink-700 p-1 font-ui">
        {tabLabels(d?.counts).map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={show === t.key}
            onClick={() => setParams(t.key === 'to_approve' ? {} : { show: t.key }, { replace: true })}
            className={`min-h-9 rounded-[7px] px-3 text-[13px] font-semibold ${focus} ${show === t.key ? 'bg-ink-500 text-paper' : 'text-paper-dim hover:text-paper'}`}>
            {t.label}{t.count !== null && <span className="ml-1.5 font-medium opacity-70">{t.count}</span>}
          </button>
        ))}
      </div>
      {q.isLoading && <p className="m-0 font-ui text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 font-ui text-radar-red">{describeError(q.error)}</p>}
      {d && d.pieces.length === 0 && <p className="m-0 font-ui text-paper-dim">Nothing here.</p>}
      {d?.pieces.map((p) => (
        <section key={p.id} className="flex flex-col gap-3.5 rounded-xl border border-ink-500 bg-ink-700 px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <div className="flex flex-col gap-1">
              <Link to={pieceHref(section.path, p.id)} className={`font-serif text-[21px] font-medium text-paper no-underline hover:underline ${focus}`}>{p.title ?? '(untitled)'}</Link>
              <span className="font-ui text-[13px] text-paper-dim">
                {p.live_since ? `Live since ${etDate(p.live_since)} ${etTime(p.live_since)}` : 'Not live yet'}
              </span>
            </div>
            {canPostApproved(d.posting_on, p.posts) && (
              <button type="button" className={btnPrimary} disabled={all.isPending} onClick={() => all.mutate(p.id)}>
                Post the {p.posts.filter((x) => x.status === 'approved' && x.publishable).length} approved now
              </button>
            )}
          </div>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))]">
            {p.posts.map((post) => <PostCard key={post.platform} draftId={p.id} post={post} postingOn={d.posting_on} onChange={refresh} />)}
          </div>
        </section>
      ))}
      {all.isError && <p role="alert" className="m-0 font-ui text-radar-red">{describeError(all.error)}</p>}
    </div>
  )
}
